/**
 * PageLayout - Unified page wrapper for all Room apps
 *
 * Provides standardized:
 * - Authentication checks with loading states
 * - Automatic tile registration for homescreen
 * - QueryClient, Toast, and Notification providers
 * - AppHeader context
 * - GeneralLayout wrapper
 * - Bottom navigation bar (optional)
 * - Error boundaries
 *
 * Usage:
 * ```tsx
 * <PageLayout
 *   pageId="chat"
 *   title="Chat"
 *   icon="💬"
 *   requireAuth={true}
 *   left={<Sidebar />}
 *   right={<Content />}
 *   showNavigation={true}
 *   navigationConfig={{ showHomeButton: true }}
 * />
 * ```
 */

import { type ReactNode, useEffect, useMemo } from 'react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { getSessionToken } from '@room/frontend-sdk';
import { LoadingScreen } from '../components/LoadingScreen';
import { useSession } from '../stores/session';
import { registerHomeTile } from '../stores/homescreen';
import { getLoginRedirectUrl, shouldRedirectToLogin } from '../utils/auth';
import { isBundleMode } from '../utils/bundleMode';
import { GeneralLayout, type GeneralLayoutProps } from './GeneralLayout';
import { AppHeaderProvider } from './AppHeaderContext';
import { NavigationProvider, type NavigationBarConfig } from '../contexts/NavigationContext';

export type PageLayoutProps = {
  /** Unique page identifier for tile registration */
  pageId: string;

  /** Page title (displayed in tile and header) */
  title: string;

  /** Page icon emoji for tile */
  icon: string;

  /** Route path for navigation (defaults to /pageId) */
  route?: string;

  /** Require authentication to view this page */
  requireAuth?: boolean;

  /** Left sidebar content */
  left?: ReactNode;

  /** Main content area */
  right?: ReactNode;

  /** Single-panel mode (no sidebar) - only renders right */
  singlePanel?: boolean;

  /** Additional GeneralLayout props */
  layoutProps?: Omit<GeneralLayoutProps, 'left' | 'right'>;

  /** Custom QueryClient instance (optional) */
  queryClient?: QueryClient;

  /** Custom loading screen */
  loadingScreen?: ReactNode;

  /** Custom unauthenticated screen */
  unauthenticatedScreen?: ReactNode;

  /** Tile section (defaults to 'main') */
  tileSection?: 'main' | 'dock';

  /** Tile order (defaults to 50) */
  tileOrder?: number;

  /** Skip automatic tile registration */
  skipTileRegistration?: boolean;

  /** Show bottom navigation bar (defaults to false) */
  showNavigation?: boolean;

  /** Navigation bar configuration */
  navigationConfig?: NavigationBarConfig;
};

// Shared QueryClient for all pages (can be overridden per page)
const defaultQueryClient = new QueryClient({
  defaultOptions: {
    queries: {
      refetchOnWindowFocus: false,
      retry: 1,
      staleTime: 30000, // 30 seconds
    },
  },
});

export function PageLayout({
  pageId,
  title,
  icon,
  route,
  requireAuth = true,
  left,
  right,
  singlePanel = false,
  layoutProps,
  queryClient = defaultQueryClient,
  loadingScreen,
  unauthenticatedScreen,
  tileSection = 'main',
  tileOrder = 50,
  skipTileRegistration = false,
  showNavigation = false,
  navigationConfig,
}: PageLayoutProps) {
  const { isAuthenticated, isLoading, hasCheckedSession, error, refresh } = useSession();
  const returnTo = useMemo(() => window.location.pathname + window.location.search, []);

  // Auto-register home tile
  useEffect(() => {
    if (!skipTileRegistration) {
      registerHomeTile({
        id: `home-${pageId}`,
        label: title,
        icon,
        route: route ?? `/${pageId}`,
        section: tileSection,
        order: tileOrder,
      });
    }
  }, [pageId, title, icon, route, tileSection, tileOrder, skipTileRegistration]);

  // Debug details for loading screens
  const debugDetails = (
    <ul style={{ margin: 0, paddingLeft: '16px' }}>
      <li>{`hasToken: ${Boolean(getSessionToken())}`}</li>
      <li>{`isLoading: ${isLoading}`}</li>
      <li>{`hasCheckedSession: ${hasCheckedSession}`}</li>
      <li>{`isAuthenticated: ${isAuthenticated}`}</li>
      {error ? <li>{`error: ${error.message}`}</li> : null}
    </ul>
  );

  // Handle authentication redirect
  useEffect(() => {
    if (requireAuth && hasCheckedSession && !isLoading && !isAuthenticated) {
      if (shouldRedirectToLogin(window.location.pathname)) {
        window.location.replace(getLoginRedirectUrl(returnTo));
      }
    }
  }, [requireAuth, hasCheckedSession, isAuthenticated, isLoading, returnTo]);

  // Loading state
  if (requireAuth && (!hasCheckedSession || isLoading)) {
    if (loadingScreen) {
      return <>{loadingScreen}</>;
    }
    return (
      <LoadingScreen
        title={`Loading ${title}`}
        message="Checking your session..."
        details={debugDetails}
      />
    );
  }

  // Unauthenticated state
  if (requireAuth && !isAuthenticated) {
    const redirectBlocked = isBundleMode();
    if (unauthenticatedScreen) {
      return <>{unauthenticatedScreen}</>;
    }
    return (
      <LoadingScreen
        title={redirectBlocked ? 'Sign in required' : 'Redirecting'}
        message={
          redirectBlocked
            ? 'Please sign in from the home screen before opening this page.'
            : 'Taking you to the login screen.'
        }
        details={debugDetails}
        actions={
          <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
            {error ? (
              <button
                type="button"
                onClick={() => refresh()}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  cursor: 'pointer',
                }}
              >
                Retry session check
              </button>
            ) : null}
            {redirectBlocked ? (
              <button
                type="button"
                onClick={() => window.open('/login', '_blank', 'noopener,noreferrer')}
                style={{
                  padding: '8px 12px',
                  borderRadius: '6px',
                  border: '1px solid #1d4ed8',
                  background: '#1d4ed8',
                  color: '#fff',
                  cursor: 'pointer',
                }}
              >
                Open login
              </button>
            ) : null}
          </div>
        }
      />
    );
  }

  // Render page content
  const content = singlePanel ? (
    <div
      style={{ minHeight: '100vh', display: 'flex', flexDirection: 'column' }}
      className={showNavigation ? 'general-layout--with-navigation' : ''}
    >
      {right}
    </div>
  ) : (
    <GeneralLayout
      left={left ?? <div />}
      right={right ?? <div />}
      {...layoutProps}
      className={[
        layoutProps?.className ?? '',
        showNavigation ? 'general-layout--with-navigation' : ''
      ].filter(Boolean).join(' ')}
    />
  );

  return (
    <QueryClientProvider client={queryClient}>
      <NavigationProvider
        defaultVisible={showNavigation}
        defaultConfig={navigationConfig}
      >
        <AppHeaderProvider>
          {content}
        </AppHeaderProvider>
      </NavigationProvider>
    </QueryClientProvider>
  );
}
